import { Component, Mixins, Prop } from 'vue-property-decorator'
import { mapActions, mapGetters, mapState } from 'vuex'
import { KForm, KFormAddress, KFormInput } from '@kwixeo/form-vue'
import { PaymentIntent } from '@stripe/stripe-js'
import { StripeElements } from '@stripe/stripe-js/types/stripe-js/elements-group'
import { BAlert, BOverlay, BFormGroup } from 'bootstrap-vue'
import { AxiosResponse } from 'axios'
import CommandMixin from '../../mixins/CommandMixin'
import StripeMixin from '../../mixins/StripeMixin'
import PaymentSuccessful from '../PaymentSuccessful/PaymentSuccessful.vue'
import PaymentMethod from '../PaymentMethod/PaymentMethod.vue'

@Component({
  computed: {
    ...mapState('app', ['app']),
    ...mapGetters('user', ['team']),
    ...mapState('globalDatas', ['countries'])
  },
  methods: {
    ...mapActions('dynamicModals', ['closeModal']),
    ...mapActions('user', ['setTeam']),
    ...mapActions('globalDatas', ['loadCountries'])
  },
  components: {
    KForm, KFormAddress, KFormInput,
    BAlert, BOverlay, BFormGroup,
    PaymentMethod,
    PaymentSuccessful
  }
})
export default class Command extends Mixins(CommandMixin, StripeMixin) {
  app!: KwixeoFramework.App
  team!: KwixeoFramework.Team
  setTeam!: (team: KwixeoFramework.Team) => void
  countries!: KwixeoFramework.Countries
  loadCountries!: (force?: boolean) => void

  @Prop({ required: true }) command!: KwixeoSubscriptions.Command

  private loading = false
  private errorMsg: string|null = null

  private elements: StripeElements|null = null
  private paymentIntent: PaymentIntent|null = null

  private name: string|null = null
  private address: KwixeoFramework.BaseAddress = {
    road1: null,
    road2: null,
    road3: null,
    postal_code: null,
    town: null,
    country_id: null,
    country: null,
  }
  private addressSaved = false

  async mounted (): Promise<void> {
    await this.loadCountries()
    this.initializeInvoiceDatas()
  }

  // Intialize the team invoice datas
  private initializeInvoiceDatas (): void {
    const team = this.team
    const teamSubscription = this.team.subscription

    let address
    if (teamSubscription?.name || teamSubscription?.road1 || teamSubscription?.road2 || teamSubscription?.road3 ||
      teamSubscription?.postal_code || teamSubscription?.town || teamSubscription?.country) {
      this.name = teamSubscription.name
      address = teamSubscription
    } else {
      this.name = team.company_name
      address = team
    }

    this.address = {
      road1: address.road1,
      road2: address.road2,
      road3: address.road3,
      postal_code: address.postal_code,
      town: address.town,
      country_id: address.country_id,
      country: address.country,
    }
  }

  // When the invoice address is saved
  private async addressSuccessfullySaved (response: AxiosResponse): Promise<void> {
    const team = JSON.parse(JSON.stringify(this.team))
    team.subscription = response.data.subscription
    this.setTeam(team)

    this.addressSaved = true
    await this.initializeStripe()
    this.loading = false
    this.initializePaymentForm()
  }

  // Initialize the payment form
  private initializePaymentForm (): void {
    if (!this.stripe || this.team.subscription?.card_type) {
      return
    }
    this.elements = this.stripe.elements({
      clientSecret: this.command.client_secret,
      appearance: {
        variables: {
          fontSizeBase: '.9rem'
        }
      }
    })
    const paymentElement = this.elements.create('payment', {
      fields: {
        billingDetails: 'never'
      },
      wallets: {
        applePay: 'never',
        googlePay: 'never'
      }
    })
    paymentElement.mount(this.$refs.paymentElement as HTMLDivElement)
  }

  // Send the payment form
  private async confirm (): Promise<void> {
    const stripe = this.stripe
    const teamSubscription = this.team.subscription as KwixeoSubscriptions.TeamSubscription

    if (!stripe) {
      this.errorMsg = 'Erreur lors de l\'initialisation du formulaire de paiement'
      return
    }

    if (teamSubscription.card_type) {
      this.loading = true
      const { error, paymentIntent } = await stripe.confirmCardPayment(this.command.client_secret, {
        payment_method: teamSubscription.stripe_payment_method_id as string
      })
      this.loading = false

      if (error) {
        this.errorMsg = error.message ?? 'Une erreur est survenue lors du paiement'
        return
      } else if (paymentIntent) {
        this.paymentIntent = paymentIntent
      }
      return
    }

    const elements = this.elements
    if (!elements) {
      this.errorMsg = 'Erreur lors de l\'initialisation du formulaire de paiement'
      return
    }

    this.loading = true
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: window.location.protocol + '//' + window.location.host + '/configuration/subscription/checkout',
        payment_method_data: {
          billing_details: {
            address: {
              city: teamSubscription.town ?? '',
              country: teamSubscription.country?.code,
              line1: teamSubscription.road1 ?? '',
              line2: teamSubscription.road2 ?? '',
              postal_code: teamSubscription.postal_code ?? '',
              state: ''
            },
            email: '',
            name: teamSubscription.name ?? '',
            phone: ''
          }
        }
      }
    })
    this.loading = false

    if (error) {
      this.errorMsg = error.message ?? 'Une erreur est survenue lors du paiement'
      return
    } else if (paymentIntent) {
      this.paymentIntent = paymentIntent
    }
  }
}
